import { useMemo } from 'react'

import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import type {
  StoreFrontDto,
  StoreFrontOptions,
} from '@b-stock/listing-api-client'

import { FeatureFlags } from '@/helpers/featureFlags'
import { useIsPremierBuyer } from '@/helpers/useIsPremierBuyer'

import AllowedShipmentTypesFilter from './filters/AllowedShipmentTypesFilter'
import buildSimpleStringFilter from './filters/buildSimpleStringFilter'
import BuyingFormatFilter from './filters/BuyingFormatFilter'
import CapacityFilter from './filters/CapacityFilter'
import CarrierFilter from './filters/CarrierFilter'
import CategoryFilter, { CategoryTreeFilter } from './filters/CategoryFilter'
import ConditionFilter from './filters/ConditionFilter'
import CurrentBidFilter from './filters/CurrentBidFilter'
import DealsFilter from './filters/DealsFilter'
import DistanceRangeFilter from './filters/DistanceRangeFilter'
import EligibleOnlyFilter from './filters/EligibleOnlyFilter'
import InventoryTypeFilter from './filters/InventoryTypeFilter'
import LandedCostPercentageFilter from './filters/LandedCostPercentageFilter'
import ListingTypeFilter from './filters/ListingTypeFilter'
import ManufacturerFilter from './filters/Manufacturer'
import MemoryFilter from './filters/MemoryFilter'
import ModelFilter from './filters/ModelFilter'
import MsrpFilter from './filters/MsrpFilter/MsrpFilter'
import MsrpPercentageFilter from './filters/MsrpPercentageFilter'
import NumberOfUnitsFilter from './filters/NumberOfUnitsFilter/NumberOfUnitsFilter'
import { buildProblemFreeOrderRateFilter } from './filters/ProblemFreeOrderRateFilter'
import ProductTypeFilter from './filters/ProductTypeFilter'
import RegionFilter from './filters/RegionFilter'
import SearchQueryFilter from './filters/SearchQueryFilter'
import SellerFilter from './filters/SellerFilter'
import SellerGradeFilter from './filters/SellerGradeFilter/SellerGradeFilter'
import StarterLotsFilter from './filters/StarterLotsFilter'
import TransportModeFilter from './filters/TransportModeFilter'
import WarehouseLocationFilter from './filters/WarehouseLocationFilter'
import type { AllListingsFiltersResult, ListingFilterList } from './types'

// TODO(GLOB-5120): remove cast after @b-stock/listing-api-client is bumped
// isPforExcluded is required (boolean, not ?) so the cast to this type is non-redundant.
type StoreFrontOptionsWithPfor = StoreFrontOptions & { isPforExcluded: boolean }

// Note: Several "Rating" filters were previously implemented, but the code has since been
//       deleted. Some of this old code might be useful if/when we resurrect those filters.
//       The MR that removed the code can be found here:
//       https://gitlab.bstock.io/b-stock/code/three-mp/fe/buyer-portal/-/merge_requests/715

// The "Accepts Credit Card" checkbox embedded in `BuyingFormatFilter` and its
// `isAlwaysDisplayed` behavior are scoped to the ALP only (BP-1833) and gated
// behind ENABLE_CC_SURFACING. Storefront sidebars — and the ALP when the flag
// is off — use this plain buying-format filter, which renders only when the
// facet returns options.
export const PlainBuyingFormatFilter = buildSimpleStringFilter({
  key: 'buyingFormat',
  expandedOnInit: true,
})

export function useAllListingsFilters(
  storefront?: StoreFrontDto
): AllListingsFiltersResult {
  const enableSubcategoryFilter = useFlag(
    storefront
      ? FeatureFlags.ENABLE_SELLER_STOREFRONT_SUBCATEGORY_FILTER
      : FeatureFlags.ENABLE_ALL_LISTINGS_SUBCATEGORY_FILTER
  )
  const enableDealsFilter = useFlag(FeatureFlags.ENABLE_DEALS_FILTER)
  const storefrontDealsFilter = useFlag(
    FeatureFlags.ENABLE_SELLER_STOREFRONT_DEALS_FILTER
  )
  const showMsrpPercentageFilter = useFlag(
    FeatureFlags.SHOW_MSRP_PERCENTAGE_FILTER
  )
  const showLandedCostPercentageFilter = useFlag(
    FeatureFlags.SHOW_LANDED_COST_PERCENTAGE_FILTER
  )
  const isPremierBuyer = useIsPremierBuyer()
  const problemFreeOrderRateConfig = useFlag(
    FeatureFlags.ENABLE_ALP_PROBLEM_FREE_ORDER_RATE
  )
  const sellerGradeFilterFlag = useFlag(
    FeatureFlags.ENABLE_STOREFRONT_SELLER_GRADE_FILTER
  )

  const enableMemoryRollupVisibility = useFlag(
    FeatureFlags.ENABLE_MEMORY_ROLLUP_VISIBILITY
  )

  const isNewBuyerListingsEnabled = useFlag(
    FeatureFlags.ENABLE_NEW_BUYER_LISTINGS
  )

  const enableCcSurfacing = useFlag(FeatureFlags.ENABLE_CC_SURFACING)
  const pforExclusionEnabled = useFlag(
    FeatureFlags.ENABLE_PFOR_STOREFRONT_EXCLUSION
  )

  const enableEligibleOnlyFilter = useFlag(
    FeatureFlags.ENABLE_ELIGIBLE_ONLY_FILTER
  )

  return useMemo(() => {
    const showLandedCostFilter =
      showLandedCostPercentageFilter && isPremierBuyer

    const isStorefrontSellerGradeEnabled =
      !!storefront &&
      !!sellerGradeFilterFlag &&
      sellerGradeFilterFlag.enabled &&
      ((!!storefront._id &&
        !!sellerGradeFilterFlag.includedStorefrontIds?.includes(
          storefront._id
        )) ||
        (!!storefront.slug &&
          !!sellerGradeFilterFlag.includedStorefrontSlugs?.includes(
            storefront.slug
          )))

    const showDealsFilter = storefront
      ? storefrontDealsFilter.enabled &&
        !(
          storefront._id &&
          storefrontDealsFilter.excludedStorefrontIds?.includes(storefront._id)
        ) &&
        !(
          storefront.slug &&
          storefrontDealsFilter.excludedStorefrontSlugs?.includes(
            storefront.slug
          )
        )
      : enableDealsFilter

    const problemFreeOrderRateFilter = buildProblemFreeOrderRateFilter(
      problemFreeOrderRateConfig.variant ?? 'c'
    )

    // TODO(GLOB-5120): remove cast after @b-stock/listing-api-client is bumped
    const storefrontOptionsWithPfor = storefront?.options as unknown as
      | StoreFrontOptionsWithPfor
      | undefined

    if (storefront) {
      const storefrontFilters: ListingFilterList = [
        ...(problemFreeOrderRateConfig.enabled &&
        (!pforExclusionEnabled ||
          storefrontOptionsWithPfor?.isPforExcluded !== true)
          ? [problemFreeOrderRateFilter]
          : []),
        ...(showDealsFilter ? [DealsFilter] : []),
        ...(isStorefrontSellerGradeEnabled ? [SellerGradeFilter] : []),
        enableSubcategoryFilter ? CategoryTreeFilter : CategoryFilter,
        ...(isStorefrontSellerGradeEnabled ? [] : [ConditionFilter]),
        WarehouseLocationFilter,
        PlainBuyingFormatFilter,
        ModelFilter,
        ManufacturerFilter,
        CapacityFilter,
        ...(enableMemoryRollupVisibility ? [MemoryFilter] : []),
        CarrierFilter,
        CurrentBidFilter,
        MsrpFilter,
        ...(showMsrpPercentageFilter ? [MsrpPercentageFilter] : []),
        ...(showLandedCostFilter ? [LandedCostPercentageFilter] : []),
        TransportModeFilter,
        AllowedShipmentTypesFilter,
        ListingTypeFilter,
        InventoryTypeFilter,
        NumberOfUnitsFilter,
        ProductTypeFilter,
        SearchQueryFilter,
      ]
      return { filters: storefrontFilters, hiddenFilters: new Set() }
    }

    // All Auctions: top-9 highest-usage filters are visible by default;
    // the rest collapse behind the See-all toggle. Mobile shares this
    // hook and ignores the partition (it renders the full list).
    const visible: ListingFilterList = [
      ...(problemFreeOrderRateConfig.enabled
        ? [problemFreeOrderRateFilter]
        : []),
      DistanceRangeFilter,
      enableSubcategoryFilter ? CategoryTreeFilter : CategoryFilter,
      ConditionFilter,
      RegionFilter,
      SellerFilter,
      // CC checkbox is gated behind ENABLE_CC_SURFACING (BP-1833); when off the
      // ALP falls back to the plain buying-format filter.
      enableCcSurfacing ? BuyingFormatFilter : PlainBuyingFormatFilter,
      TransportModeFilter,
      AllowedShipmentTypesFilter,
      InventoryTypeFilter,
    ]

    const hidden: ListingFilterList = [
      ...(showDealsFilter ? [DealsFilter] : []),
      WarehouseLocationFilter,
      ModelFilter,
      ManufacturerFilter,
      CapacityFilter,
      ...(enableMemoryRollupVisibility ? [MemoryFilter] : []),
      CarrierFilter,
      CurrentBidFilter,
      MsrpFilter,
      ...(showMsrpPercentageFilter ? [MsrpPercentageFilter] : []),
      ...(showLandedCostFilter ? [LandedCostPercentageFilter] : []),
      ListingTypeFilter,
      NumberOfUnitsFilter,
      SearchQueryFilter,
      ...(isNewBuyerListingsEnabled ? [StarterLotsFilter] : []),
      ...(enableEligibleOnlyFilter ? [EligibleOnlyFilter] : []),
    ]

    return {
      filters: [...visible, ...hidden],
      hiddenFilters: new Set(hidden),
    }
  }, [
    enableDealsFilter,
    storefrontDealsFilter,
    enableSubcategoryFilter,
    showMsrpPercentageFilter,
    showLandedCostPercentageFilter,
    isPremierBuyer,
    problemFreeOrderRateConfig,
    sellerGradeFilterFlag,
    enableMemoryRollupVisibility,
    isNewBuyerListingsEnabled,
    enableCcSurfacing,
    pforExclusionEnabled,
    enableEligibleOnlyFilter,
    storefront,
  ])
}
